import { withErrorBoundary } from "../../../../shared-components/ErrorBoundary";
import "../HelpContent.css";
import { Icon } from "../../WorkflowsScope";

const NebulaSupportAgentHelpC: React.FC<any> = (props: any) => {
  return (
    <>
      <div>
        <div style={{ marginBottom: "18px" }}>
          <h3>Nebula Support Agent</h3>
        </div>

        {/* 1. Overview */}
        <div className="help-mainHeading">
          <span className="help-heading" style={{ fontSize: "18px" }}>Overview:</span>
          <span className="help-content">
            The Nebula Self-Serve Support Agent is an AI-powered conversational assistant embedded directly in the
            Nebula Self-Serve portal. The agent helps tenants independently
            troubleshoot issues, resolve workflow failures, and receive onboarding guidance — without requiring manual
            intervention from Nebula DRIs.
          </span>
          <span className="help-content" style={{ marginTop: "8px", display: "block" }}>
            The agent leverages Nebula's internal Troubleshooting Guides (TSGs) and onboarding documentation to provide
            accurate, contextual, and actionable responses in natural language. When a workflow is selected on the portal,
            the agent automatically receives its context (workflow name and job ID) to provide targeted diagnostics.
          </span>
          <span className="help-content" style={{ marginTop: "8px", display: "block" }}>
            All responses are AI-generated and clearly labeled as such. The agent is scoped strictly to Nebula platform
            operations, subject to Azure AI content safety filters across all harm categories, and does not access cross-tenant data.
          </span>
        </div>
        <hr />

        {/* 2. Features at a Glance */}
        <div className="help-mainHeading">
          <span className="help-heading" style={{ fontSize: "18px" }}>Features at a Glance:</span>
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "8px" }}>
            <thead>
              <tr style={{ backgroundColor: "#f0f0f0", textAlign: "left" }}>
                <th style={{ padding: "8px", border: "1px solid #ddd", fontWeight: "500" }}>Feature</th>
                <th style={{ padding: "8px", border: "1px solid #ddd", fontWeight: "500" }}>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: "8px", border: "1px solid #ddd" }}><b style={{ fontWeight: "500" }}>Autonomous Cluster Issue Resolution</b></td>
                <td style={{ padding: "8px", border: "1px solid #ddd" }}>Automatically detects cluster-related workflow failures, swaps the cluster, and reruns the workflow. For non-cluster errors, explains the root cause and provides step-by-step mitigation.</td>
              </tr>
              <tr>
                <td style={{ padding: "8px", border: "1px solid #ddd" }}><b style={{ fontWeight: "500" }}>TSG-Based Troubleshooting</b></td>
                <td style={{ padding: "8px", border: "1px solid #ddd" }}>Searches Nebula's internal Troubleshooting Guides (TSGs) to answer platform issues including API errors, data connection failures, configuration mismatches, and more.</td>
              </tr>
              <tr>
                <td style={{ padding: "8px", border: "1px solid #ddd" }}><b style={{ fontWeight: "500" }}>Onboarding Assistance</b></td>
                <td style={{ padding: "8px", border: "1px solid #ddd" }}>Guides new tenants through workflow creation, data connections, attribute mapping, App ID whitelisting, and API consumption — reducing manual DRI involvement significantly.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <hr />

        {/* 3. How to Access */}
        <div className="help-mainHeading">
          <span className="help-heading" style={{ fontSize: "18px" }}>How to Access the Support Agent:</span>
          <ol>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">Navigate to the Nebula Self-Serve portal.</span>
            </li>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">
                Locate the <Icon name="robot" style={{ fontSize: "14px" }} /> Support Agent icon in the top navigation bar (AppHeader). Click it to open the agent panel.
              </span>
            </li>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">
                The agent chat panel will open as a fly-in panel on the right side of the screen. You can now type your query.
              </span>
            </li>
          </ol>
        </div>
        <hr />

        {/* 4. Autonomous Cluster Issue Resolution */}
        <div className="help-mainHeading">
          <span className="help-heading" style={{ fontSize: "18px" }}>Feature 1: Autonomous Cluster Issue Resolution</span>
          <span className="help-content" style={{ marginTop: "8px", display: "block" }}>
            When a workflow is selected on the portal, the agent automatically receives the workflow context (workflow name
            and job ID). If the workflow is failing due to a cluster issue, the agent will automatically swap the cluster
            and rerun the workflow. For other failures, the agent explains the error and provides step-by-step mitigation guidance.
          </span>
          <span className="help-heading" style={{ fontSize: "16px", marginTop: "12px", display: "block" }}>Steps to Use:</span>
          <ol>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">Navigate to the <b style={{ fontWeight: "500" }}>Insights and Recommendations</b> tab.</span>
            </li>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">Select the workflow that is failing or needs investigation.</span>
            </li>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">Open the Support Agent panel.</span>
            </li>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">Type your query in natural language. Examples:</span>
              <ul style={{ marginTop: "4px" }}>
                <li><i>"Why is this workflow failing?"</i></li>
                <li><i>"This workflow is not running, what's wrong?"</i></li>
                <li><i>"Fix the cluster issue for this workflow."</i></li>
              </ul>
            </li>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">The agent will analyze the workflow context and take the appropriate action:</span>
              <ul style={{ marginTop: "4px" }}>
                <li><b style={{ fontWeight: "500" }}>Cluster issue:</b> The agent automatically swaps the cluster, reruns the workflow, and confirms the action.</li>
                <li><b style={{ fontWeight: "500" }}>Other errors:</b> The agent explains the root cause and provides step-by-step mitigation guidance.</li>
              </ul>
            </li>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">Review the agent's response and follow any additional steps if prompted.</span>
            </li>
          </ol>
          <span className="help-heading" style={{ fontSize: "16px", marginTop: "12px", display: "block" }}>Important Notes:</span>
          <ul>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">The workflow must be selected on the portal before querying the agent for it to receive workflow-specific context.</span>
            </li>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">All agent actions (such as cluster swap) are clearly confirmed before and after execution.</span>
            </li>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">If the issue persists after following the agent's guidance, contact <b>nebulaengg@microsoft.com</b>.</span>
            </li>
          </ul>
        </div>
        <hr />

        {/* 5. TSG-Based Troubleshooting */}
        <div className="help-mainHeading">
          <span className="help-heading" style={{ fontSize: "18px" }}>Feature 2: TSG-Based Troubleshooting</span>
          <span className="help-content" style={{ marginTop: "8px", display: "block" }}>
            The agent has access to Nebula's internal Troubleshooting Guides (TSGs) covering a wide range of known platform issues.
            Tenants can ask about errors, configuration issues, API failures, data connection problems, and more — in plain natural language.
            The agent always searches the knowledge base first before responding.
            If a query is not covered, the agent will explicitly say so and recommend contacting the Nebula team.
          </span>
          <span className="help-heading" style={{ fontSize: "16px", marginTop: "12px", display: "block" }}>Common Troubleshooting Scenarios:</span>
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "8px" }}>
            <thead>
              <tr style={{ backgroundColor: "#f0f0f0", textAlign: "left" }}>
                <th style={{ padding: "8px", border: "1px solid #ddd", fontWeight: "500" }}>Issue Type</th>
                <th style={{ padding: "8px", border: "1px solid #ddd", fontWeight: "500" }}>Example Query</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: "8px", border: "1px solid #ddd" }}>API Authentication Errors</td>
                <td style={{ padding: "8px", border: "1px solid #ddd" }}><i>"I'm getting a 401 error when calling the Nebula API. How do I fix it?"</i></td>
              </tr>
              <tr>
                <td style={{ padding: "8px", border: "1px solid #ddd" }}>Invalid App Auth (OData)</td>
                <td style={{ padding: "8px", border: "1px solid #ddd" }}><i>"I'm getting an 'Invalid App Auth in OData Route' error."</i></td>
              </tr>
              <tr>
                <td style={{ padding: "8px", border: "1px solid #ddd" }}>Data Connection Issues</td>
                <td style={{ padding: "8px", border: "1px solid #ddd" }}><i>"My ADLS connection is failing. What could be wrong?"</i></td>
              </tr>
              <tr>
                <td style={{ padding: "8px", border: "1px solid #ddd" }}>Impersonation Errors</td>
                <td style={{ padding: "8px", border: "1px solid #ddd" }}><i>"I'm getting an impersonation error. What does that mean?"</i></td>
              </tr>
              <tr>
                <td style={{ padding: "8px", border: "1px solid #ddd" }}>App ID Whitelisting</td>
                <td style={{ padding: "8px", border: "1px solid #ddd" }}><i>"How do I whitelist my App ID in Nebula Self-Serve?"</i></td>
              </tr>
            </tbody>
          </table>
          <span className="help-heading" style={{ fontSize: "16px", marginTop: "12px", display: "block" }}>Steps to Use:</span>
          <ol>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">Open the Support Agent panel from the top navigation bar.</span>
            </li>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">Optionally, select the relevant workflow to provide context. The agent can answer troubleshooting queries with or without a workflow selected.</span>
            </li>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">Type your issue or question in natural language. Be as specific as possible, including the error message or code if available.</span>
            </li>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">The agent will search the Nebula TSG knowledge base and return documented resolution steps.</span>
            </li>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">Follow the steps provided. If the issue is not covered, the agent will direct you to contact <b>nebulaengg@microsoft.com</b>.</span>
            </li>
          </ol>
          <span className="help-heading" style={{ fontSize: "16px", marginTop: "12px", display: "block" }}>Tips for Better Results:</span>
          <ul>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">Include the specific error message or error code you are seeing in your query.</span>
            </li>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">Ask follow-up questions in the same chat session — the agent maintains conversation context.</span>
            </li>
          </ul>
        </div>
        <hr />

        {/* 6. Onboarding Assistance */}
        <div className="help-mainHeading">
          <span className="help-heading" style={{ fontSize: "18px" }}>Feature 3: Onboarding Assistance</span>
          <span className="help-content" style={{ marginTop: "8px", display: "block" }}>
            New tenants onboarding to Nebula can use the Support Agent as a self-serve onboarding companion.
            The agent can answer onboarding questions, guide through setup steps, and significantly reduce the need
            for manual DRI support. This is especially valuable for first-time tenants who need guidance without
            waiting for engineer availability.
          </span>
          <span className="help-heading" style={{ fontSize: "16px", marginTop: "12px", display: "block" }}>What You Can Ask:</span>
          <ul>
            <li style={{ marginBottom: "6px" }}><span className="help-content">How to create a new workflow (Insight or Recommendation)</span></li>
            <li style={{ marginBottom: "6px" }}><span className="help-content">How to set up data connections (MSI, SPN, SPN+FIC)</span></li>
            <li style={{ marginBottom: "6px" }}><span className="help-content">How to configure the Refine, Publish, and Consume steps in a workflow</span></li>
            <li style={{ marginBottom: "6px" }}><span className="help-content">How to onboard and whitelist an App ID on Self-Serve</span></li>
            <li style={{ marginBottom: "6px" }}><span className="help-content">How to consume recommendations and insights via Nebula APIs</span></li>
            <li style={{ marginBottom: "6px" }}><span className="help-content">How to schedule and deploy a workflow on Azure Databricks (ADB)</span></li>
            <li style={{ marginBottom: "6px" }}><span className="help-content">What merge rules are and how to configure them</span></li>
          </ul>
          <span className="help-heading" style={{ fontSize: "16px", marginTop: "12px", display: "block" }}>Steps to Use:</span>
          <ol>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">Open the Support Agent panel on the Nebula Self-Serve portal.</span>
            </li>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">Type your onboarding question in natural language. Examples:</span>
              <ul style={{ marginTop: "4px" }}>
                <li><i>"How do I create a new recommendation workflow?"</i></li>
                <li><i>"What data connection types does Nebula support?"</i></li>
                <li><i>"How do I whitelist my App ID?"</i></li>
                <li><i>"How do I consume recommendations after the workflow runs successfully?"</i></li>
              </ul>
            </li>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">The agent will provide step-by-step guidance sourced from Nebula's onboarding documentation.</span>
            </li>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">Follow the steps provided. You can ask follow-up questions in the same session for further clarification.</span>
            </li>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">If the agent cannot answer your question, it will direct you to contact <b>nebulaengg@microsoft.com</b>.</span>
            </li>
          </ol>
          <span className="help-heading" style={{ fontSize: "16px", marginTop: "12px", display: "block" }}>Why Use the Agent for Onboarding?</span>
          <ul>
            <li style={{ marginBottom: "6px" }}><span className="help-content">Available 24/7 — no need to wait for office hours or engineer availability.</span></li>
            <li style={{ marginBottom: "6px" }}><span className="help-content">Provides environment-specific values and documented steps.</span></li>
            <li style={{ marginBottom: "6px" }}><span className="help-content">Reduces back-and-forth communication with the Nebula DRI team.</span></li>
            <li style={{ marginBottom: "6px" }}><span className="help-content">Ideal for new tenant teams exploring the Nebula platform for the first time.</span></li>
          </ul>
        </div>
        <hr />

        {/* 7. Responsible AI and Safety */}
        <div className="help-mainHeading">
          <span className="help-heading" style={{ fontSize: "18px" }}>Responsible AI and Safety:</span>
          <ul>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">All responses are AI-generated and clearly labeled as such within the chat panel.</span>
            </li>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">No cross-tenant data is accessible through the agent.</span>
            </li>
            <li style={{ marginBottom: "6px" }}>
              <span className="help-content">The agent operates in an advisory capacity. All recommendations require explicit human review and action before any system changes are made (except for automated actions such as cluster swap, which are confirmed to the user).</span>
            </li>
          </ul>
        </div>
        <hr />

        {/* 8. Contact and Escalation */}
        <div className="help-mainHeading">
          <span className="help-heading" style={{ fontSize: "18px" }}>Contact and Escalation:</span>
          <span className="help-content" style={{ display: "block", marginTop: "8px" }}>
            If the Support Agent cannot resolve your issue, or if the issue persists after following the agent's
            recommended steps, please reach out to the Nebula engineering team: <b>nebulaengg@microsoft.com</b>.
          </span>
        </div>
      </div>
    </>
  );
};
export const NebulaSupportAgentHelp = withErrorBoundary("NebulaSupportAgent help content", NebulaSupportAgentHelpC);
